import { RESET_MINUTES, type ResetAskedDto } from '@nsb/core';

/**
 * Forgot your password, in words (Jason, 2026-10-08: "sends a one time email link and if the email doesn't exist it
 * says so. the link brings the user to a password reset page where they enter their new password"): where a link went
 * and how long it works, why a link opened does not work, and what choosing the password did. One module, so the
 * sign-in page, Account and the reset page say it the same way; pure, so the words are tested as written. An address
 * no account uses is the API's own words (`no_account`), shown as it answers.
 */

/** A link asked for: where it went and how long it works, or that it did not go. `to` null: the address the last one came to. */
export function resetSentWords(r: ResetAskedDto, to: string | null): { line: string; meta: string } {
  const lasts = `It works once, for ${RESET_MINUTES} minutes.`;
  if (r.sent) return { line: 'Check your email for a link to choose a new password.', meta: `It went to ${to ?? 'the address the last one came to'}. ${lasts}` };
  // Development keeps its mail on this machine (mail.ts `toThisMachine`): the link is shown here instead.
  if (r.devLink) return { line: 'Not sent from this machine.', meta: to ? `Live, it goes to ${to}. ${lasts}` : lasts };
  return { line: 'The email did not go out just now.', meta: 'Try again in a few minutes.' };
}

/** Why a reset link opened does not work, and whether a new one can be sent to the address it came to. */
export function linkProblemWords(code: string | null, message: string | null): { line: string; detail: string | null; again: boolean } {
  const sendsNew = 'We can send a new one to the address it came to.';
  if (code === 'link_used') return { line: 'That link was used already.', detail: `Each link works once. ${sendsNew}`, again: true };
  if (code === 'expired') return { line: 'That link has run out.', detail: `Each link works for ${RESET_MINUTES} minutes. ${sendsNew}`, again: true };
  if (code === 'incomplete') return { line: 'This link is incomplete.', detail: 'Ask for a new one on the sign-in page.', again: false };
  if (code === 'link_unknown') return { line: 'This link does not work.', detail: 'It may be cut short, or more than a week old. Ask for a new one on the sign-in page.', again: false };
  return { line: message ?? 'Something went wrong. Try again.', detail: null, again: false };
}

/** The new password chosen: signed in here, and out everywhere else. */
export function resetDoneWords(signedOut: number): string {
  if (!signedOut) return 'You are signed in with it now.';
  return `You are signed in with it now, and signed out on your ${signedOut === 1 ? 'other device' : `${signedOut} other devices`}.`;
}
